import React, { useEffect, useState } from 'react';
import { Divider, Grid } from '@material-ui/core';
import { LoadingBounce } from '@michelin/acid-components';
import { useTranslation } from '@michelin/central-provider';
import QtySelector from 'components/QtySelector/qtySelector';
import { useCurrentLocationIsDealer } from 'hooks/useCurrentLocationIsDealer';
import { useGetPrices } from 'hooks/useGetPrices';
import { AppsType, Part, PartPrices } from 'types/UFOTypes';
import { INSTALLATION_TPMS, SERVICE_DEFAULT_QTY } from 'utils/constantValues';
import { getMspn } from 'utils/getMspn';
import { CommonPricesParamsType } from 'utils/getPricesCallCommonParams';
import { Price } from './Price';

interface ServiceInformationProps {
  service: Part | PartPrices;
  selected?: boolean;
  setSelected?: () => void;
  printMode?: boolean;
  status: string;
  qty: number;
  setQty?: (q: number) => void;
  isEditing?: boolean;
  loading?: boolean;
  setPqlByMspn?: (mspn: string, price: number) => void;
  setPqlById?: (id: number, price: number) => void;
  orderPrice?: Part;
  commonPricesParams: CommonPricesParamsType;
  // eslint-disable-next-line @typescript-eslint/naming-convention
  is_pql: boolean;
  wizardMode?: boolean;
  reqPqlMode?: boolean;
  pqlPrice?: number;
  isInstallation?: boolean;
  shoppingCartMode?: boolean;
  disabled?: boolean;
  mobileView?: boolean;
  smallSize?: boolean;
  setPart?: (parts: Part) => void;
  maxQty?: number;
}

export default function ServiceInformation(props: ServiceInformationProps) {
  const { t } = useTranslation();
  const {
    selected,
    setSelected = () => {},
    printMode = false,
    status,
    isEditing = false,
    loading = false,
    setPqlById,
    setPqlByMspn,
    orderPrice,
    commonPricesParams,
    is_pql: isPql,
    wizardMode,
    pqlPrice,
    reqPqlMode,
    isInstallation,
    shoppingCartMode,
    disabled,
    mobileView,
    smallSize,
    setPart,
    service: serviceProp,
    qty: qtyProp,
    setQty: setQtyProp,
    maxQty: maxQtyProp,
  } = props;

  // TODO: are these redundant?
  const [service, setService] = useState<Part | PartPrices>(serviceProp);
  const [qty, setQty] = useState(qtyProp);

  const pricesResponse = useGetPrices({
    ...commonPricesParams,
    // eslint-disable-next-line @typescript-eslint/naming-convention
    parts: [{ mspn: getMspn(service) as string, qty, is_pql: isPql }],
  });

  useEffect(() => {
    if (isInstallation && setPart) {
      setPart({
        ...(service as Part),
        quantity: qty,
      });
    } else if (setQtyProp) setQtyProp(qty);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [qty]);

  useEffect(() => {
    if (pricesResponse?.data?.parts?.length && !pricesResponse.isLoading && !service.description) {
      setService({ ...service, description: pricesResponse.data.parts[0].part_description || '' });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pricesResponse]);

  useEffect(() => {
    setQty(qtyProp);
  }, [qtyProp]);

  function instanceOfPart(object: Part | PartPrices): object is Part {
    return 'apps' in object;
  }
  function instanceOfPartPrices(object: Part | PartPrices): object is PartPrices {
    return 'additional_information' in object;
  }

  const { comment } = service as Part;
  let ufoAppAdditionalInfo: AppsType | undefined;
  if (instanceOfPart(service)) ufoAppAdditionalInfo = service.apps?.find((app) => app.app === 'ufo');
  else if (instanceOfPartPrices(service)) ufoAppAdditionalInfo = service;

  const qtyBlocked = ufoAppAdditionalInfo?.additional_information?.qty_linked_to_tire || false;
  const maxQty = maxQtyProp ?? ufoAppAdditionalInfo?.additional_information?.max_quantity ?? 8;
  const description = ufoAppAdditionalInfo?.description || service.description || service.part_description || '';

  const isDealer = useCurrentLocationIsDealer();
  const removable = (!isInstallation || isDealer || getMspn(service) === INSTALLATION_TPMS) && !disabled && isEditing;

  type CustomGridSize = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12;

  let priceWidth: CustomGridSize = 3;
  if (shoppingCartMode || mobileView) priceWidth = 6;
  if (isInstallation && shoppingCartMode) priceWidth = 7;
  if (mobileView && isInstallation) priceWidth = 7;
  if (reqPqlMode) priceWidth = 5;

  let descriptionWidth: CustomGridSize = 12;
  if (!mobileView && !shoppingCartMode) descriptionWidth = 6;
  if (isInstallation && !mobileView) descriptionWidth = (descriptionWidth - 2) as CustomGridSize;

  let qtyWidth: CustomGridSize = 3;
  if (shoppingCartMode || mobileView) {
    qtyWidth = 6;
  } else if (reqPqlMode) {
    qtyWidth = 1;
  }

  const selectQty = (
    <div
      style={{
        transform: mobileView || shoppingCartMode || isInstallation || smallSize ? 'scale(0.8)' : undefined,
        transformOrigin: 'center left',
      }}
    >
      <QtySelector
        value={qty}
        onAdd={setSelected}
        onRemove={setSelected}
        maxValue={Number(maxQty)}
        defaultValue={SERVICE_DEFAULT_QTY}
        onChange={(v) => {
          if (setQty) setQty(v);
        }}
        selected={!!selected}
        disabled={!isEditing || qtyBlocked || !removable}
        shoppingCartMode={shoppingCartMode}
      />
    </div>
  );

  return (
    <>
      <Grid
        container
        spacing={2}
        style={{
          opacity: (isInstallation && !isEditing) || disabled ? 0.6 : undefined,
          position: shoppingCartMode ? 'relative' : undefined,
        }}
      >
        {isInstallation && <Grid item xs={2} />}
        <Grid item xs={descriptionWidth}>
          <span style={{ fontSize: isInstallation || smallSize ? 10 : 14, fontWeight: 'bold' }}>
            {t(description)}
            <br />
          </span>
          <span
            style={{ fontSize: isInstallation || smallSize ? 9 : 12 }}
            id={shoppingCartMode ? 'cartServiceNumber' : 'serviceNumber'}
          >
            <b>{t('Part #: ')}</b>
            {getMspn(service)}
            {service.dealer_part_id && <>{`|${service.dealer_part_id}`}</>}
          </span>
          {comment && (
            <>
              <br />
              <span style={{ fontSize: isInstallation || smallSize ? 9 : 12 }}>{t(comment)}</span>
            </>
          )}
        </Grid>
        {isInstallation && (shoppingCartMode || mobileView) && <Grid item xs={2} />}
        <Grid item xs={qtyWidth} style={{ textAlign: 'left' }}>
          {!loading && selectQty}
        </Grid>
        <Grid
          item
          xs={priceWidth}
          style={
            smallSize || isInstallation
              ? {
                  textAlign: 'right',
                  display: 'flex',
                  justifyContent: 'flex-end',
                }
              : { textAlign: 'right' }
          }
        >
          <div
            style={
              isInstallation || smallSize
                ? { transform: 'scale(0.8)', display: 'flex', transformOrigin: 'center right' }
                : undefined
            }
          >
            <Price
              isService
              printMode={printMode}
              status={status}
              qty={qty}
              setPqlByMspn={setPqlByMspn}
              setPqlById={setPqlById}
              canEdit={isEditing || wizardMode || reqPqlMode}
              isEditing={isEditing}
              mspn={getMspn(service) as string}
              prices={orderPrice}
              commonPricesParams={commonPricesParams}
              is_pql={isPql}
              wizardMode={wizardMode}
              reqPqlMode={reqPqlMode}
              pqlPrice={pqlPrice}
              submitButton
              manualPrice={service.manual_pricing}
              id={service.id}
            />
            {loading && <LoadingBounce />}
          </div>
        </Grid>
        {/* {!shoppingCartMode && !mobileView && (
          <Grid item xs={3}>
            {!loading && selectQty}
          </Grid>
        )} */}
        {!isInstallation && (
          <Divider
            id="serviceInformationDivider"
            style={{
              width: '100%',
              marginTop: 16,
              marginBottom: 16,
            }}
          />
        )}
      </Grid>
    </>
  );
}
